import React, { useMemo, useState } from 'react';
import './QuizCard.css';

// Map a free-form "correct answer" value onto an option index. The AI (or a
// human author) may express the answer as a 0-based index, a 1-based number, a
// letter (A/B/C…), or the verbatim option text — accept all of them.
function resolveCorrectIndex(rawAnswer, options) {
  if (rawAnswer === undefined || rawAnswer === null) return -1;
  if (typeof rawAnswer === 'number' && Number.isFinite(rawAnswer)) {
    if (rawAnswer >= 0 && rawAnswer < options.length) return rawAnswer; // 0-based
    if (rawAnswer >= 1 && rawAnswer <= options.length) return rawAnswer - 1; // 1-based
    return -1;
  }
  const str = String(rawAnswer).trim();
  if (!str) return -1;
  // Single letter -> A=0, B=1, …
  if (/^[A-Za-z]$/.test(str)) {
    const idx = str.toUpperCase().charCodeAt(0) - 65;
    if (idx >= 0 && idx < options.length) return idx;
  }
  // Plain number string
  if (/^\d+$/.test(str)) {
    return resolveCorrectIndex(Number(str), options);
  }
  // Verbatim option text (case-insensitive)
  const lower = str.toLowerCase();
  const byText = options.findIndex((o) => String(o).trim().toLowerCase() === lower);
  return byText;
}

/**
 * Normalise the many shapes a quiz might arrive in into a single canonical
 * structure: { title, questions: [{ question, options:[string], correctIndex,
 * explanation }] }. Returns null when the content can't be understood, so the
 * caller can fall back to showing the raw text.
 */
export function normalizeQuiz(content) {
  let data = content;
  if (typeof content === 'string') {
    try {
      data = JSON.parse(content);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== 'object') return null;
  const rawQuestions = Array.isArray(data) ? data : data.questions || data.items;
  if (!Array.isArray(rawQuestions) || rawQuestions.length === 0) return null;

  const questions = [];
  for (const q of rawQuestions) {
    if (!q || typeof q !== 'object') continue;
    const prompt = q.question || q.q || q.prompt || q.text;
    const optionSource = q.options || q.choices || q.answers;
    if (!prompt || !Array.isArray(optionSource) || optionSource.length < 2) continue;

    // Options may be plain strings or objects carrying a `correct` flag.
    let correctIndex = -1;
    const options = optionSource.map((o, i) => {
      if (o && typeof o === 'object') {
        if (o.correct === true || o.isCorrect === true) correctIndex = i;
        return String(o.text ?? o.label ?? o.value ?? '');
      }
      return String(o);
    });
    if (correctIndex === -1) {
      const rawAnswer = q.answer ?? q.correct ?? q.correctIndex ?? q.correctAnswer ?? q.solution;
      correctIndex = resolveCorrectIndex(rawAnswer, options);
    }

    questions.push({
      question: String(prompt),
      options,
      correctIndex,
      explanation: q.explanation || q.rationale || q.reason || '',
    });
  }

  if (questions.length === 0) return null;
  return { title: typeof data.title === 'string' ? data.title : '', questions };
}

/**
 * Interactive quiz rendered from a quiz artifact's JSON content. Shows the
 * questions, lets the reader pick an answer per question, then grades on submit
 * with per-question correctness, the right answer, and any explanation.
 *
 * Fully self-contained (no server round-trip) so it works identically in a
 * thread bubble and in the Artifacts panel.
 */
export default function QuizCard({ content, title }) {
  const quiz = useMemo(() => normalizeQuiz(content), [content]);
  const [selected, setSelected] = useState({});
  const [submitted, setSubmitted] = useState(false);

  if (!quiz) {
    // Couldn't parse — degrade to showing the raw text rather than nothing.
    return <pre className="quiz-raw">{String(content || '')}</pre>;
  }

  const heading = title || quiz.title;
  const total = quiz.questions.length;
  const score = quiz.questions.reduce((n, q, i) => (selected[i] === q.correctIndex ? n + 1 : n), 0);
  const allAnswered = quiz.questions.every((_, i) => selected[i] !== undefined);

  const choose = (qi, oi) => {
    if (submitted) return;
    setSelected((prev) => ({ ...prev, [qi]: oi }));
  };

  const reset = () => {
    setSelected({});
    setSubmitted(false);
  };

  return (
    <div className="quiz-card">
      {heading && <div className="quiz-card-title">{heading}</div>}
      {submitted && (
        <div
          className={`quiz-score ${score === total ? 'perfect' : ''}`}
          role="status"
          aria-live="polite"
        >
          You scored {score} / {total}
        </div>
      )}
      <ol className="quiz-questions">
        {quiz.questions.map((q, qi) => {
          const picked = selected[qi];
          return (
            <li key={qi} className="quiz-question">
              <div className="quiz-question-text">{q.question}</div>
              <div className="quiz-options" role="radiogroup" aria-label={q.question}>
                {q.options.map((opt, oi) => {
                  const isPicked = picked === oi;
                  const isCorrect = oi === q.correctIndex;
                  let state = '';
                  if (submitted) {
                    if (isCorrect) state = 'correct';
                    else if (isPicked) state = 'incorrect';
                  } else if (isPicked) {
                    state = 'picked';
                  }
                  return (
                    <button
                      type="button"
                      key={oi}
                      className={`quiz-option ${state}`}
                      role="radio"
                      aria-checked={isPicked}
                      disabled={submitted}
                      onClick={() => choose(qi, oi)}
                    >
                      <span className="quiz-option-marker" aria-hidden="true">
                        {String.fromCharCode(65 + oi)}
                      </span>
                      <span className="quiz-option-text">{opt}</span>
                      {submitted && isCorrect && (
                        <span className="quiz-option-flag" aria-hidden="true">
                          ✓
                        </span>
                      )}
                      {submitted && isPicked && !isCorrect && (
                        <span className="quiz-option-flag" aria-hidden="true">
                          ✗
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {submitted && q.explanation && (
                <div className="quiz-explanation">{q.explanation}</div>
              )}
            </li>
          );
        })}
      </ol>
      <div className="quiz-actions">
        {!submitted ? (
          <button
            type="button"
            className="quiz-submit"
            disabled={!allAnswered}
            onClick={() => setSubmitted(true)}
          >
            {allAnswered ? 'Check answers' : `Answer all ${total} to check`}
          </button>
        ) : (
          <button type="button" className="quiz-retry" onClick={reset}>
            Try again
          </button>
        )}
      </div>
    </div>
  );
}
